import { useState } from "react";
import { poolApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { vocabularyFor, countOfUnits } from "../../services/productVocabulary";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";

/**
 * Turn a product's pool into the sizes you defined (#490).
 *
 * ── THE GAP THIS CLOSES ──
 *
 * Found in validation, 6 Oct:
 *
 *     "in Wax I have 15 lbs of wax in the Wax pool but there is no way to
 *      make those 15 lbs into the 1 Lb Block size I created. It's the
 *      same for all 3 secondary products"
 *
 * Exactly so. `ProductPoolAdjuster` offered Add and Remove and nothing in
 * between, so a size could be defined and never made. The pool was a
 * number that only went down and the sizes table was decorative — and
 * nothing packaged could be sold, because nothing packaged could exist.
 *
 * The API half has been there since #430's schema slice: POST
 * /honey/pool/bottle reads `product_type` off the chosen size. Nothing
 * called it for a non-honey product.
 *
 * ── IT SPEAKS THE PRODUCT'S OWN LANGUAGE ──
 *
 * You POUR wax into blocks, PACK pollen into jars, BOTTLE propolis as
 * tincture. `vocabularyFor` already holds those words and the page around
 * this uses them, so a control saying "Bottle" on a wax card would be the
 * one place that reverted to honey's vocabulary.
 *
 * ── NO VARIETAL PICKER, DELIBERATELY ──
 *
 * Honey's version asks which varietal is leaving the pool. Nothing should
 * ask which flower a block of wax came from, and the rest of this page
 * already follows that rule.
 *
 * ── THE OVER-DRAW QUESTION IS HONEY'S, AND IT IS KEPT ──
 *
 * #237: packaging MAY exceed the pool, because the beekeeper is usually
 * documenting work already done — the blocks are poured and on the bench.
 * Refusing does not prevent the negative, it just means the app stops
 * describing reality. So a shortfall comes back as a 200 carrying
 * `needs_confirmation`, which is a question rather than an error, and the
 * re-post carries explicit consent.
 *
 * NEVER RE-POSTED AUTOMATICALLY. A mistyped count is far likelier than a
 * genuine over-draw, and a client that retried by itself would authorise
 * one on the user's behalf.
 */
export function ProductPackager({ productType, sizes = [], onChanged, open, onClose, mode = "pack" }) {
  /* CONTROLLED MODAL (#518). The card owns the buttons and the More menu,
     like the Honey Pool's, and says which form is open. mode="unpack" is
     the inverse (Unpour / Unpack / Unbottle): the server's unbottle call
     takes the product from the size, so it already works for wax, propolis
     and pollen. It refuses more than is on the shelf, and says so. */
  const unpack = mode === "unpack";
  const setOpen = (v) => { if (!v) onClose?.(); };
  const { notify } = useToast();
  const vocab = vocabularyFor(productType);

  const [form, setForm] = useState({ bottle_size_id: "", bottle_count: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [shortfall, setShortfall] = useState(null);

  const usable = sizes.filter((s) => s.is_active !== false);
  /* Nothing to package INTO. The empty state belongs to the sizes table
     above, which already explains how to add one -- a second empty
     message here would be the same sentence twice on one card. */
  if (usable.length === 0) return null;

  async function submit(acknowledgeShortfall) {
    const count = Number(form.bottle_count);
    /* `count > 0`, not `!count`: a falsy check catches 0 and NaN and lets
       -5 through, because -5 is truthy. Honey's form records the same
       trap, having shipped it once. */
    if (!form.bottle_size_id || !(count > 0)) {
      return notify(`Pick a size and how many ${vocab.units}`, "warning");
    }

    setBusy(true);
    try {
      if (unpack) {
        await poolApi.unbottle(Number(form.bottle_size_id), count, form.reason || undefined);
        setOpen(false);
        setForm({ bottle_size_id: "", bottle_count: "", reason: "" });
        notify(`${countOfUnits(productType, count)} emptied back into the pool`, "success");
        onChanged?.();
        return;
      }
      const r = await poolApi.bottle(
        Number(form.bottle_size_id), count, form.reason || undefined,
        /* No varietal. The server fills in its own default, and for a
           non-honey product it is never asked about again. */
        undefined,
        acknowledgeShortfall,
      );

      if (r.needs_confirmation === "shortfall") {
        setShortfall(r);
        return;
      }

      setShortfall(null);
      setOpen(false);
      setForm({ bottle_size_id: "", bottle_count: "", reason: "" });
      /* "poured", "packed", "bottled" -- the raw state without its prefix,
         which is the idiom ProductPoolAdjuster already uses rather than a
         second list of past tenses to keep in step. */
      const done = vocab.raw.replace(/^un/, "");
      notify(`${countOfUnits(productType, count)} ${done}`, "success");
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || `Could not ${unpack ? "un" : ""}${vocab.verb.toLowerCase()} that`, "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={unpack ? `Empty ${vocab.units} back into the pool` : `${vocab.verb} into ${vocab.units}`}
        footer={(
          <Button disabled={busy} onClick={() => submit(false)}>
            {busy ? "Saving…" : "Save"}
          </Button>
        )}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="Size">
            {(id, aria) => (
              <select
                id={id} {...aria} className="touch-target"
                value={form.bottle_size_id}
                onChange={(e) => setForm((f) => ({ ...f, bottle_size_id: e.target.value }))}
              >
                <option value="">Choose a size…</option>
                {usable.map((s) => (
                  <option key={s.bottle_size_id} value={s.bottle_size_id}>{s.label}</option>
                ))}
              </select>
            )}
          </Field>

          <Field label={`How many ${vocab.units}`}>
            {(id, aria) => (
              <input
                id={id} {...aria} type="number" min="1" className="touch-target"
                value={form.bottle_count}
                onChange={(e) => setForm((f) => ({ ...f, bottle_count: e.target.value }))}
              />
            )}
          </Field>

          <Field label="Note (optional)">
            {(id, aria) => (
              <input
                id={id} {...aria} className="touch-target" value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              />
            )}
          </Field>
        </div>
      </Modal>

      {/*
        * MORE THAN THE POOL RECORDS (#237). A question, not a refusal --
        * see the header. The numbers to ask with came back with the 200,
        * so the dialog states the gap rather than making the user work it
        * out.
        */}
      <ConfirmDialog
        open={Boolean(shortfall)}
        title={`That is more ${productType.toLowerCase()} than the pool records`}
        confirmLabel={`${vocab.verb} anyway`}
        onConfirm={() => { setShortfall(null); submit(true); }}
        onClose={() => setShortfall(null)}
      >
        {shortfall ? (
          <p style={{ margin: 0 }}>
            The pool holds {Number(shortfall.available_pounds ?? 0).toFixed(3)} lbs and this
            would use {Number(shortfall.required_pounds ?? 0).toFixed(3)} lbs. Recording it
            anyway leaves the pool at{" "}
            {Number(shortfall.resulting_pool_pounds ?? 0).toFixed(3)} lbs, which is a sign the
            balance was already behind rather than a reason not to write this down.
          </p>
        ) : null}
      </ConfirmDialog>

    </>
  );
}
